import { createFileRoute } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import { aboutPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

export const Route = createFileRoute("/about")({
  head: () =>
    seo({
      title: "About Axy.digital — Creating the category of AI native marketing",
      description:
        "Axy.digital is the AI native marketing platform used by 1,200+ brands to run SEO and GEO on autopilot.",
      path: "/about",
      jsonLd: [
        aboutPage({
          name: "About Axy.digital",
          description:
            "Built by marketers and AI engineers for the next era of go-to-market: autonomous, signal-driven, always adapting.",
          url: `${SITE_URL}/about`,
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "About", url: "/about" },
        ]),
      ],
    }),
  component: AboutPage,
});

const principles = [
  {
    title: "Built around signals, not guesses",
    body: [
      "Most marketing starts with assumptions. Axy.digital starts with live market data.",
      "We track what customers are searching, what competitors are saying, what narratives are gaining traction, and where attention is moving across search, AI discovery, social channels, and industry conversations.",
      "Every campaign begins with evidence of demand, not a blank page.",
    ],
  },
  {
    title: "No-prompt by default",
    body: [
      "Prompting is powerful, but it is not a workflow.",
      "Most AI tools still make users do the hardest part: deciding what to ask, what matters, what to create, and how to improve it.",
      "Axy.digital is designed to reduce that burden. The system learns your business, understands your market, and proactively recommends what to publish next. You can configure, refine, and steer the engine — but you should not have to start from scratch every time.",
    ],
  },
  {
    title: "Relevance over volume",
    body: [
      "More content is not the answer. The right content is.",
      "Axy.digital is built for an era where visibility depends on being cited, referenced, and recommended across both search engines and AI systems. We optimize for authority, not noise.",
      "The goal is not to publish more. The goal is to become the brand your market keeps finding.",
    ],
  },
  {
    title: "Autonomy over management",
    body: [
      "If you have to manage the AI, it is not saving you enough time.",
      "Axy.digital is designed to run the marketing loop for you: from signal detection to strategy, content, distribution, and learning.",
      "You stay in control of the direction. Axy.digital handles the execution — so you can spend your time building your business, not managing your marketing stack.",
    ],
  },
];

const team = [
  {
    name: "Robin Lim",
    role: "CEO, Co-founder",
    bio: [
      "Robin has been building and investing in startups since 2014. She has led GTM and growth for 30+ Web3 and AI companies, helping technical teams turn complex products into clear positioning, campaigns, and market momentum.",
      "Before \u201Cvibe marketing\u201D had a name, Robin was already building the systems behind it: signal-led strategy, fast iteration loops, and marketing that adapts to the market instead of waiting for the next planning cycle.",
    ],
    quote: "I was a marketer before vibe marketing.",
  },
  {
    name: "Floriane Le Floch",
    role: "CTO, Co-founder",
    bio: [
      "Floriane has been building enterprise-grade AI systems since 2015, serving Fortune 500 companies across energy, defence, construction, and other complex industries.",
      "Her work has focused on building AI systems that can operate in high-stakes environments: structured knowledge, decision support, automation, and reliable execution across complex workflows.",
      "At Axy.digital, she leads the development of the algorithmic marketing engine: the intelligence layer that turns market signals into autonomous go-to-market execution.",
    ],
  },
];

function AboutPage() {
  return (
    <div className="px-6">
      {/* Hero */}
      <section className="max-w-4xl mx-auto pt-24 pb-20">
        <p className="text-xs font-mono uppercase tracking-tighter text-muted-foreground mb-6">
          About Axy.digital
        </p>
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-pretty">
          Creating the category of AI native marketing.
        </h1>
        <div className="mt-10 space-y-5 text-lg text-muted-foreground max-w-3xl text-pretty">
          <p className="text-foreground font-medium">
            Axy.digital is a live AI native marketing platform used by 1,200+ brands to run their SEO
            and GEO on autopilot.
          </p>
          <p>
            Marketing is becoming too fast, too fragmented, and too expensive for humans to manage
            manually.
          </p>
          <p>
            Markets now move at machine speed. Customers search across Google, ChatGPT, Perplexity,
            LinkedIn, X, Reddit, and dozens of niche channels. Narratives shift daily. Competitors
            publish constantly. New demand appears and disappears before most teams have even
            approved the content calendar.
          </p>
          <p>
            But most marketing teams are still operating on human-paced systems: strategy decks,
            monthly retainers, manual research, scattered tools, and endless prompting.
          </p>
          <p className="text-foreground font-medium">We built Axy.digital for a different reality.</p>
        </div>
      </section>

      {/* Vision */}
      <section className="max-w-4xl mx-auto py-16 border-t border-border">
        <p className="text-xs font-mono uppercase tracking-tighter text-muted-foreground mb-6">
          Our vision
        </p>
        <div className="space-y-5 text-lg text-muted-foreground max-w-3xl text-pretty">
          <p>
            Axy.digital is creating the category of{" "}
            <span className="text-foreground font-medium">algorithmic marketing</span>: a new way
            for companies to detect demand, understand what matters, and execute campaigns
            automatically while the opportunity is still active.
          </p>
          <p>
            Axy.digital is an AI marketing engine that detects market signals in real time, turns
            them into strategy, generates campaigns, distributes content, and learns from
            performance.
          </p>
          <p>
            1,200+ businesses already run their marketing on a system that understands their market,
            acts at the speed of demand, and compounds over time.
          </p>
          <blockquote className="border-l-2 border-primary pl-6 py-2 text-xl text-foreground font-medium">
            Every business should have access to a marketing system that understands its market,
            acts at the speed of demand, and compounds over time.
          </blockquote>
          <p>
            Axy.digital is built for the next era of go-to-market: autonomous, signal-driven, and
            always adapting.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-6xl mx-auto py-20 border-t border-border">
        <div className="max-w-4xl">
          <p className="text-xs font-mono uppercase tracking-tighter text-muted-foreground mb-6">
            Design principles
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Four principles behind the engine.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 gap-px bg-border border border-border rounded-lg overflow-hidden">
          {principles.map((p, i) => (
            <div key={p.title} className="bg-background p-8 md:p-10">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-xs font-mono text-muted-foreground">0{i + 1}</span>
                <h3 className="text-xl font-semibold tracking-tight">{p.title}</h3>
              </div>
              <div className="space-y-3 text-muted-foreground text-pretty">
                {p.body.map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Team intro */}
      <section className="max-w-4xl mx-auto py-20 border-t border-border">
        <p className="text-xs font-mono uppercase tracking-tighter text-muted-foreground mb-6">
          About the team
        </p>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-pretty">
          Built by marketers and AI engineers who saw the same problem from opposite sides.
        </h2>
        <div className="mt-8 space-y-5 text-lg text-muted-foreground text-pretty">
          <p>
            Axy.digital was founded by operators who have spent years building at the intersection of
            marketing, AI, automation, and emerging technology.
          </p>
          <p>
            We started with a simple frustration: marketing had become too expensive, too slow, and
            too fragmented for the pace of modern markets.
          </p>
          <p>
            Before Axy.digital, we worked with startups, scaleups, and enterprise teams across AI, Web3,
            SaaS, energy, defence, construction, and emerging technology markets. Again and again,
            we saw the same pattern: strategy was scattered, data was underused, execution was
            manual, and teams were paying too much for too little speed.
          </p>
          <p className="text-foreground font-medium">So we built the system we wished existed.</p>
        </div>
      </section>

      {/* Founders */}
      <section className="max-w-6xl mx-auto pb-24">
        <div className="grid md:grid-cols-2 gap-px bg-border border border-border rounded-lg overflow-hidden">
          {team.map((member) => (
            <article key={member.name} className="bg-background p-8 md:p-10">
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight">{member.name}</h3>
                  <p className="text-sm font-mono text-muted-foreground mt-1">{member.role}</p>
                </div>
              </div>
              <div className="space-y-4 text-muted-foreground text-pretty">
                {member.bio.map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
              </div>
              {member.quote && (
                <p className="mt-6 pt-6 border-t border-border text-foreground italic">
                  &ldquo;{member.quote}&rdquo;
                </p>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
